import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Palette, Type, Maximize, Eye, CheckCircle, RotateCcw, Save, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Presets
const THEME_PRESETS = [
  {
    id: 'default',
    name: 'Ocean Blue',
    description: 'Professional blue theme',
    light_colors: {
      primary: '221.2 83.2% 53.3%',
      secondary: '210 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '210 40% 96.1%',
      accent: '210 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '214.3 31.8% 91.4%'
    },
    dark_colors: {
      primary: '217.2 91.2% 59.8%',
      secondary: '217.2 32.6% 17.5%',
      background: '222.2 84% 4.9%',
      foreground: '210 40% 98%',
      card: '222.2 84% 4.9%',
      muted: '217.2 32.6% 17.5%',
      accent: '217.2 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '217.2 32.6% 17.5%'
    }
  },
  {
    id: 'purple',
    name: 'Royal Purple',
    description: 'Modern and elegant purple',
    light_colors: {
      primary: '262.1 83.3% 57.8%',
      secondary: '270 40% 96%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '270 40% 96%',
      accent: '270 40% 96%',
      destructive: '0 84.2% 60.2%',
      border: '270 31.8% 91.4%'
    },
    dark_colors: {
      primary: '263.4 70% 50.4%',
      secondary: '270 32.6% 17.5%',
      background: '270 50% 4.9%',
      foreground: '270 40% 98%',
      card: '270 50% 4.9%',
      muted: '270 32.6% 17.5%',
      accent: '270 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '270 32.6% 17.5%'
    }
  },
  {
    id: 'emerald',
    name: 'Emerald Green',
    description: 'Fresh and natural green',
    light_colors: {
      primary: '142.1 76.2% 36.3%',
      secondary: '142 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '142 40% 96.1%',
      accent: '142 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '142 31.8% 91.4%'
    },
    dark_colors: {
      primary: '142.1 70.6% 45.3%',
      secondary: '142 32.6% 17.5%',
      background: '142 50% 4.9%',
      foreground: '142 40% 98%',
      card: '142 50% 4.9%',
      muted: '142 32.6% 17.5%',
      accent: '142 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '142 32.6% 17.5%'
    }
  },
  {
    id: 'sunset',
    name: 'Sunset Orange',
    description: 'Warm and energetic orange',
    light_colors: {
      primary: '24.6 95% 53.1%',
      secondary: '24 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '24 40% 96.1%',
      accent: '24 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '24 31.8% 91.4%'
    },
    dark_colors: {
      primary: '20.5 90.2% 48.2%',
      secondary: '24 32.6% 17.5%',
      background: '24 50% 4.9%',
      foreground: '24 40% 98%',
      card: '24 50% 4.9%',
      muted: '24 32.6% 17.5%',
      accent: '24 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '24 32.6% 17.5%'
    }
  },
  {
    id: 'rose',
    name: 'Rose Pink',
    description: 'Soft and creative pink',
    light_colors: {
      primary: '346.8 77.2% 49.8%',
      secondary: '347 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '347 40% 96.1%',
      accent: '347 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '347 31.8% 91.4%'
    },
    dark_colors: {
      primary: '346.8 77.2% 49.8%',
      secondary: '347 32.6% 17.5%',
      background: '347 50% 4.9%',
      foreground: '347 40% 98%',
      card: '347 50% 4.9%',
      muted: '347 32.6% 17.5%',
      accent: '347 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '347 32.6% 17.5%'
    }
  },
  {
    id: 'slate',
    name: 'Slate Gray',
    description: 'Minimal and professional',
    light_colors: {
      primary: '222.2 47.4% 11.2%',
      secondary: '210 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '210 40% 96.1%',
      accent: '210 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '214.3 31.8% 91.4%'
    },
    dark_colors: {
      primary: '210 40% 80%',
      secondary: '217.2 32.6% 17.5%',
      background: '222.2 84% 4.9%',
      foreground: '210 40% 98%',
      card: '222.2 84% 4.9%',
      muted: '217.2 32.6% 17.5%',
      accent: '217.2 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '217.2 32.6% 17.5%'
    }
  },
  {
    id: 'teal',
    name: 'Teal Cyan',
    description: 'Cool and calming teal',
    light_colors: {
      primary: '173.4 80.4% 40%',
      secondary: '173 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '173 40% 96.1%',
      accent: '173 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '173 31.8% 91.4%'
    },
    dark_colors: {
      primary: '172.5 66% 50.4%',
      secondary: '173 32.6% 17.5%',
      background: '173 50% 4.9%',
      foreground: '173 40% 98%',
      card: '173 50% 4.9%',
      muted: '173 32.6% 17.5%',
      accent: '173 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '173 32.6% 17.5%'
    }
  },
  {
    id: 'amber',
    name: 'Amber Gold',
    description: 'Rich and luxurious amber',
    light_colors: {
      primary: '32.1 94.6% 43.7%',
      secondary: '32 40% 96.1%',
      background: '0 0% 100%',
      foreground: '222.2 84% 4.9%',
      card: '0 0% 100%',
      muted: '32 40% 96.1%',
      accent: '32 40% 96.1%',
      destructive: '0 84.2% 60.2%',
      border: '32 31.8% 91.4%'
    },
    dark_colors: {
      primary: '37.7 92.1% 50.2%',
      secondary: '32 32.6% 17.5%',
      background: '32 50% 4.9%',
      foreground: '32 40% 98%',
      card: '32 50% 4.9%',
      muted: '32 32.6% 17.5%',
      accent: '32 32.6% 17.5%',
      destructive: '0 62.8% 30.6%',
      border: '32 32.6% 17.5%'
    }
  }
];

export const ThemeEditor = () => {
  const { refreshTheme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [draftTheme, setDraftTheme] = useState(null);
  const [publishedTheme, setPublishedTheme] = useState(null);
  const [previewMode, setPreviewMode] = useState('draft'); // 'draft' or 'published'

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    setLoading(true);
    try {
      const [draftRes, publishedRes] = await Promise.all([
        axios.get(`${API}/theme/draft`),
        axios.get(`${API}/theme?draft=false`)
      ]);
      
      setDraftTheme(draftRes.data);
      setPublishedTheme(publishedRes.data);
    } catch (error) {
      console.error('Error fetching themes:', error);
      toast.error('Failed to load theme configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleColorChange = (mode, key, value) => {
    setDraftTheme(prev => ({
      ...prev,
      [mode === 'light' ? 'light_colors' : 'dark_colors']: {
        ...prev[mode === 'light' ? 'light_colors' : 'dark_colors'],
        [key]: value
      }
    }));
  };

  const handleTypographyChange = (key, value) => {
    setDraftTheme(prev => ({
      ...prev,
      typography: {
        ...prev.typography,
        [key]: value
      }
    }));
  };

  const handleSpacingChange = (key, value) => {
    setDraftTheme(prev => ({
      ...prev,
      spacing: {
        ...prev.spacing,
        [key]: value
      }
    }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/theme/draft`, draftTheme);
      setDraftTheme(response.data);
      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!window.confirm('Are you sure you want to publish this theme? This will make it live for all users.')) {
      return;
    }

    setPublishing(true);
    try {
      const response = await axios.post(`${API}/theme/publish`);
      setPublishedTheme(response.data);
      toast.success('Theme published successfully! 🎉 Refreshing platform...');
      await fetchThemes(); // Refresh both themes
      
      // Apply the new theme to the platform immediately
      await refreshTheme();
      toast.success('Platform theme updated!');
    } catch (error) {
      console.error('Error publishing theme:', error);
      toast.error('Failed to publish theme');
    } finally {
      setPublishing(false);
    }
  };

  const handleResetDraft = async () => {
    if (!window.confirm('Are you sure you want to reset the draft to match the published theme? All unsaved changes will be lost.')) {
      return;
    }

    try {
      const response = await axios.post(`${API}/theme/reset-draft`);
      setDraftTheme(response.data);
      toast.success('Draft reset to published version');
    } catch (error) {
      console.error('Error resetting draft:', error);
      toast.error('Failed to reset draft');
    }
  };

  const applyPreset = (preset) => {
    setDraftTheme(prev => ({
      ...prev,
      light_colors: { ...preset.light_colors },
      dark_colors: { ...preset.dark_colors }
    }));
    toast.success(`Applied ${preset.name} theme preset`);
  };

  const ThemePresetCard = ({ preset }) => {
    const primaryColor = preset.light_colors.primary;
    const backgroundColor = preset.light_colors.background;
    const cardColor = preset.light_colors.card;
    
    return (
      <Card 
        className="cursor-pointer hover:ring-2 hover:ring-primary transition-all"
        onClick={() => applyPreset(preset)}
      >
        <CardContent className="p-4">
          <div className="space-y-3">
            {/* Theme Preview */}
            <div className="h-24 rounded-md overflow-hidden border-2 border-border">
              <div className="h-full flex">
                {/* Light mode half */}
                <div 
                  className="w-1/2 p-2 flex flex-col gap-1"
                  style={{ backgroundColor: `hsl(${backgroundColor})` }}
                >
                  <div 
                    className="h-2 rounded"
                    style={{ backgroundColor: `hsl(${primaryColor})` }}
                  />
                  <div 
                    className="h-2 w-3/4 rounded"
                    style={{ backgroundColor: `hsl(${primaryColor})`, opacity: 0.6 }}
                  />
                  <div 
                    className="flex-1 rounded mt-1"
                    style={{ backgroundColor: `hsl(${cardColor})`, border: '1px solid hsl(214.3 31.8% 91.4%)' }}
                  />
                </div>
                {/* Dark mode half */}
                <div 
                  className="w-1/2 p-2 flex flex-col gap-1"
                  style={{ backgroundColor: `hsl(${preset.dark_colors.background})` }}
                >
                  <div 
                    className="h-2 rounded"
                    style={{ backgroundColor: `hsl(${preset.dark_colors.primary})` }}
                  />
                  <div 
                    className="h-2 w-3/4 rounded"
                    style={{ backgroundColor: `hsl(${preset.dark_colors.primary})`, opacity: 0.6 }}
                  />
                  <div 
                    className="flex-1 rounded mt-1"
                    style={{ backgroundColor: `hsl(${preset.dark_colors.card})`, border: '1px solid hsl(217.2 32.6% 17.5%)' }}
                  />
                </div>
              </div>
            </div>
            
            {/* Theme Info */}
            <div>
              <h4 className="font-semibold text-sm">{preset.name}</h4>
              <p className="text-xs text-muted-foreground">{preset.description}</p>
            </div>
            
            {/* Apply Button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={(e) => {
                e.stopPropagation();
                applyPreset(preset);
              }}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Apply Theme
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const ColorInput = ({ label, value, onChange, mode }) => {
    // Parse HSL values to get individual components
    const parseHSL = (hslString) => {
      if (!hslString) return { h: 220, s: 91, l: 60 };
      const parts = hslString.trim().split(/\s+/);
      return {
        h: parseInt(parts[0]) || 220,
        s: parseInt(parts[1]) || 91,
        l: parseInt(parts[2]) || 60
      };
    };

    const { h, s, l } = parseHSL(value);

    // Convert HSL to hex for color picker
    const hslToHex = (h, s, l) => {
      l /= 100;
      const a = s * Math.min(l, 1 - l) / 100;
      const f = n => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
      };
      return `#${f(0)}${f(8)}${f(4)}`;
    };

    // Convert hex to HSL
    const hexToHSL = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16) / 255;
      const g = parseInt(hex.slice(3, 5), 16) / 255;
      const b = parseInt(hex.slice(5, 7), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h, s, l = (max + min) / 2;

      if (max === min) {
        h = s = 0;
      } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }

      return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
      };
    };

    const handleColorPickerChange = (e) => {
      const hex = e.target.value;
      const hsl = hexToHSL(hex);
      onChange(`${hsl.h} ${hsl.s}% ${hsl.l}%`);
    };

    const currentHex = hslToHex(h, s, l);
    const previewStyle = { backgroundColor: `hsl(${h}, ${s}%, ${l}%)` };

    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">{label}</Label>
        <div className="flex gap-2 items-center">
          <Input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="217 91% 60%"
            className="flex-1 font-mono text-sm"
          />
          <input
            type="color"
            value={currentHex}
            onChange={handleColorPickerChange}
            className="w-12 h-10 rounded border-2 border-border cursor-pointer"
            title="Pick a color"
          />
          <div
            className="w-12 h-10 rounded border-2 border-border"
            style={previewStyle}
            title="Preview"
          />
        </div>
        <p className="text-xs text-muted-foreground">
          HSL: {h}° {s}% {l}% • Hex: {currentHex}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading theme editor...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Palette className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-3xl font-bold">Theme Editor</h1>
              <p className="text-muted-foreground">Customize platform colors, typography, and spacing</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewMode(previewMode === 'draft' ? 'published' : 'draft')}
            >
              <Eye className="h-4 w-4 mr-2" />
              Preview: {previewMode === 'draft' ? 'Draft' : 'Published'}
            </Button>
            <Button variant="outline" onClick={handleResetDraft}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset Draft
            </Button>
            <Button variant="outline" onClick={handleSaveDraft} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              <CheckCircle className="h-4 w-4 mr-2" />
              {publishing ? 'Publishing...' : 'Publish Live'}
            </Button>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Badge variant="secondary">Draft</Badge>
                Working Version
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {draftTheme?.name || 'Untitled Theme'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Last updated: {draftTheme?.updated_at ? new Date(draftTheme.updated_at).toLocaleString() : 'Never'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Badge variant="default">Published</Badge>
                Live Version
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                {publishedTheme?.name || 'No published theme'}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Last published: {publishedTheme?.updated_at ? new Date(publishedTheme.updated_at).toLocaleString() : 'Never'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Editor Tabs */}
        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="presets">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="presets">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Presets
                </TabsTrigger>
                <TabsTrigger value="colors">
                  <Palette className="h-4 w-4 mr-2" />
                  Colors
                </TabsTrigger>
                <TabsTrigger value="typography">
                  <Type className="h-4 w-4 mr-2" />
                  Typography
                </TabsTrigger>
                <TabsTrigger value="spacing">
                  <Maximize className="h-4 w-4 mr-2" />
                  Spacing
                </TabsTrigger>
              </TabsList>

              {/* Presets Tab */}
              <TabsContent value="presets" className="space-y-6 mt-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2">Theme Presets</h3>
                    <p className="text-sm text-muted-foreground">
                      Choose from professionally designed color schemes. Click any preset to apply it to your draft theme.
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {THEME_PRESETS.map(preset => (
                      <ThemePresetCard key={preset.id} preset={preset} />
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
                    <Sparkles className="h-5 w-5 text-primary" />
                    <p className="text-sm text-muted-foreground">
                      After applying a preset, you can customize individual colors in the Colors tab.
                    </p>
                  </div>
                </div>
              </TabsContent>

              {/* Colors Tab */}
              <TabsContent value="colors" className="space-y-6 mt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Light Mode Colors */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-300"></div>
                      Light Mode Colors
                    </h3>
                    <div className="space-y-4">
                      <ColorInput
                        label="Primary (Brand Color)"
                        value={draftTheme?.light_colors?.primary || '221.2 83.2% 53.3%'}
                        onChange={(val) => handleColorChange('light', 'primary', val)}
                      />
                      <ColorInput
                        label="Secondary"
                        value={draftTheme?.light_colors?.secondary || '210 40% 96.1%'}
                        onChange={(val) => handleColorChange('light', 'secondary', val)}
                      />
                      <ColorInput
                        label="Background"
                        value={draftTheme?.light_colors?.background || '0 0% 100%'}
                        onChange={(val) => handleColorChange('light', 'background', val)}
                      />
                      <ColorInput
                        label="Foreground (Text)"
                        value={draftTheme?.light_colors?.foreground || '222.2 84% 4.9%'}
                        onChange={(val) => handleColorChange('light', 'foreground', val)}
                      />
                      <ColorInput
                        label="Card Background"
                        value={draftTheme?.light_colors?.card || '0 0% 100%'}
                        onChange={(val) => handleColorChange('light', 'card', val)}
                      />
                      <ColorInput
                        label="Muted (Subtle BG)"
                        value={draftTheme?.light_colors?.muted || '210 40% 96.1%'}
                        onChange={(val) => handleColorChange('light', 'muted', val)}
                      />
                      <ColorInput
                        label="Accent"
                        value={draftTheme?.light_colors?.accent || '210 40% 96.1%'}
                        onChange={(val) => handleColorChange('light', 'accent', val)}
                      />
                      <ColorInput
                        label="Destructive (Error/Delete)"
                        value={draftTheme?.light_colors?.destructive || '0 84.2% 60.2%'}
                        onChange={(val) => handleColorChange('light', 'destructive', val)}
                      />
                      <ColorInput
                        label="Border"
                        value={draftTheme?.light_colors?.border || '214.3 31.8% 91.4%'}
                        onChange={(val) => handleColorChange('light', 'border', val)}
                      />
                    </div>
                  </div>

                  {/* Dark Mode Colors */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-gray-900 border-2 border-gray-600"></div>
                      Dark Mode Colors
                    </h3>
                    <div className="space-y-4">
                      <ColorInput
                        label="Primary (Brand Color)"
                        value={draftTheme?.dark_colors?.primary || '217.2 91.2% 59.8%'}
                        onChange={(val) => handleColorChange('dark', 'primary', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Secondary"
                        value={draftTheme?.dark_colors?.secondary || '217.2 32.6% 17.5%'}
                        onChange={(val) => handleColorChange('dark', 'secondary', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Background"
                        value={draftTheme?.dark_colors?.background || '222.2 84% 4.9%'}
                        onChange={(val) => handleColorChange('dark', 'background', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Foreground (Text)"
                        value={draftTheme?.dark_colors?.foreground || '210 40% 98%'}
                        onChange={(val) => handleColorChange('dark', 'foreground', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Card Background"
                        value={draftTheme?.dark_colors?.card || '222.2 84% 4.9%'}
                        onChange={(val) => handleColorChange('dark', 'card', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Muted (Subtle BG)"
                        value={draftTheme?.dark_colors?.muted || '217.2 32.6% 17.5%'}
                        onChange={(val) => handleColorChange('dark', 'muted', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Accent"
                        value={draftTheme?.dark_colors?.accent || '217.2 32.6% 17.5%'}
                        onChange={(val) => handleColorChange('dark', 'accent', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Destructive (Error/Delete)"
                        value={draftTheme?.dark_colors?.destructive || '0 62.8% 30.6%'}
                        onChange={(val) => handleColorChange('dark', 'destructive', val)}
                        mode="dark"
                      />
                      <ColorInput
                        label="Border"
                        value={draftTheme?.dark_colors?.border || '217.2 32.6% 17.5%'}
                        onChange={(val) => handleColorChange('dark', 'border', val)}
                        mode="dark"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Typography Tab */}
              <TabsContent value="typography" className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Font Family</Label>
                    <Input
                      value={draftTheme?.typography?.font_family || ''}
                      onChange={(e) => handleTypographyChange('font_family', e.target.value)}
                      placeholder="Font family"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Base Font Size</Label>
                    <Input
                      value={draftTheme?.typography?.font_size_base || ''}
                      onChange={(e) => handleTypographyChange('font_size_base', e.target.value)}
                      placeholder="16px"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Small Font Size</Label>
                    <Input
                      value={draftTheme?.typography?.font_size_sm || ''}
                      onChange={(e) => handleTypographyChange('font_size_sm', e.target.value)}
                      placeholder="14px"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Large Font Size</Label>
                    <Input
                      value={draftTheme?.typography?.font_size_lg || ''}
                      onChange={(e) => handleTypographyChange('font_size_lg', e.target.value)}
                      placeholder="18px"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Spacing Tab */}
              <TabsContent value="spacing" className="space-y-4 mt-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Border Radius</Label>
                    <Input
                      value={draftTheme?.spacing?.radius || ''}
                      onChange={(e) => handleSpacingChange('radius', e.target.value)}
                      placeholder="0.5rem"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Container Padding</Label>
                    <Input
                      value={draftTheme?.spacing?.container_padding || ''}
                      onChange={(e) => handleSpacingChange('container_padding', e.target.value)}
                      placeholder="1.5rem"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Section Spacing</Label>
                    <Input
                      value={draftTheme?.spacing?.section_spacing || ''}
                      onChange={(e) => handleSpacingChange('section_spacing', e.target.value)}
                      placeholder="3rem"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
